// The "day view": entries for the selected date grouped by meal, with a day
// nutrient summary, per-meal subtotals, add/edit/delete controls. Rendered
// inline beside the calendar (master–detail).
import React, { useState } from 'react'
import { Box, Stack, Heading, Text, Card, Button, IconButton, Badge, Spinner, Wrap, SimpleStat, ConfirmButton } from '@/components/lib'
import { FaPlus, FaTrash, FaCheck, FaPen } from 'react-icons/fa'
import api from '../../../api'
import utils from '../../../utils/utils'
import FoodDiaryAddFood from './FoodDiaryAddFood'
import { MEALS, NUTRIENT_DISPLAY, formatDateLong, sumNutrients, portionText } from './foodDiaryShared'

function nutrientChips (nutrients) {
  return NUTRIENT_DISPLAY.map(n => {
    const v = nutrients?.[n.key]
    if (typeof v !== 'number') return null
    return <Badge key={n.key} colorPalette='gray' variant='surface'>{v.toFixed(n.dp)} {n.unit === 'kcal' ? 'kcal' : n.unit}</Badge>
  }).filter(Boolean)
}

function EntryRow ({ trialId, participantId, entry, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [quantity, setQuantity] = useState(entry.quantity ?? 1)
  const [saving, setSaving] = useState(false)

  function saveQuantity () {
    setSaving(true)
    api.foodDiaries.updateEntry(trialId, participantId, entry._id, { quantity: Number(quantity) || 1 }, () => {
      setSaving(false)
      setEditing(false)
      onChanged()
    }, err => {
      setSaving(false)
      utils.toast('error', err.message)
    })
  }

  function remove () {
    api.foodDiaries.deleteEntry(trialId, participantId, entry._id, onChanged, err => utils.toast('error', err.message))
  }

  return (
    <Stack direction='row' justifyContent='space-between' alignItems='center' py={2} borderBottomWidth='1px'>
      <Box minW={0}>
        <Text fontSize='sm' fontWeight='medium' truncate>{entry.food?.description || 'Food'}</Text>
        {editing
          ? (
            <Stack direction='row' alignItems='center' gap={2} mt={1}>
              <input
                type='number' min={0} step='0.5' value={quantity}
                onChange={e => setQuantity(e.target.value)}
                style={{ width: 70, padding: '2px 6px', border: '1px solid var(--chakra-colors-border)', borderRadius: 4 }}
              />
              <Text fontSize='xs' color='fg.muted'>× {entry.portion?.size}{entry.portion?.unit === 'grams' ? 'g' : ` ${entry.portion?.unit || ''}`}</Text>
              <IconButton aria-label='Save' size='2xs' colorPalette='primary' onClick={saveQuantity} loading={saving}><FaCheck /></IconButton>
            </Stack>)
          : <Text fontSize='xs' color='fg.muted'>{portionText(entry)}</Text>}
      </Box>
      <Stack direction='row' alignItems='center' gap={2} flexShrink={0}>
        <Wrap gap={1} display={{ base: 'none', md: 'flex' }}>{nutrientChips(entry.nutrients)}</Wrap>
        {!editing && <IconButton aria-label='Edit quantity' size='2xs' variant='ghost' onClick={() => setEditing(true)}><FaPen /></IconButton>}
        <ConfirmButton
          header='Delete entry?'
          body={`Remove "${entry.food?.description || 'this food'}" from the diary?`}
          actionColor='red'
          onConfirm={remove}
          trigger={<IconButton aria-label='Delete' size='2xs' variant='ghost' colorPalette='red'><FaTrash /></IconButton>}
        />
      </Stack>
    </Stack>
  )
}

export default function FoodDiaryDayPanel ({ trialId, participantId, date, entries, loading, onChanged }) {
  const [addingMeal, setAddingMeal] = useState(null)
  const dayTotals = sumNutrients(entries)

  return (
    <Card variant='outline' gap={4}>
      <Stack direction={{ base: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ base: 'start', sm: 'center' }} gap={2}>
        <Heading size='md'>{formatDateLong(date)}</Heading>
        {loading && <Spinner size='sm' />}
      </Stack>

      {/* Day nutrient summary */}
      <Wrap gap={6}>
        {NUTRIENT_DISPLAY.map(n => (
          <SimpleStat
            key={n.key}
            name={`${n.label}${n.unit === 'kcal' ? '' : ` (${n.unit})`}`}
            number={typeof dayTotals[n.key] === 'number' ? `${dayTotals[n.key].toFixed(n.dp)}${n.unit === 'kcal' ? ' kcal' : ''}` : '–'}
          />
        ))}
      </Wrap>

      {/* Meal sections */}
      <Stack gap={4} separator={<Box borderBottomWidth='0' />}>
        {MEALS.map(meal => {
          const mealEntries = entries.filter(e => e.meal === meal.key)
          const mealTotals = sumNutrients(mealEntries)
          const kcal = mealTotals.KCALS
          return (
            <Box key={meal.key}>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' alignItems='baseline' gap={2}>
                  <Heading size='sm'>{meal.label}</Heading>
                  {typeof kcal === 'number' && <Text fontSize='sm' color='fg.muted'>{kcal.toFixed(0)} kcal</Text>}
                </Stack>
                {addingMeal !== meal.key &&
                  <Button size='xs' variant='outline' onClick={() => setAddingMeal(meal.key)}><FaPlus /> Add food</Button>}
              </Stack>

              {mealEntries.length === 0 && addingMeal !== meal.key &&
                <Text fontSize='sm' color='fg.muted' mt={1}>Nothing logged.</Text>}

              {mealEntries.map(entry => (
                <EntryRow key={entry._id} trialId={trialId} participantId={participantId} entry={entry} onChanged={onChanged} />
              ))}

              {addingMeal === meal.key &&
                <Box mt={2}>
                  <FoodDiaryAddFood
                    trialId={trialId}
                    participantId={participantId}
                    date={date}
                    meal={meal.key}
                    onSaved={() => { setAddingMeal(null); onChanged() }}
                    onCancel={() => setAddingMeal(null)}
                  />
                </Box>}
            </Box>
          )
        })}
      </Stack>
    </Card>
  )
}
